// ============================================
// AUTHENTICATION STORE (Zustand)
// ============================================

import { create } from 'zustand';
import { supabase, signInWithOtp, verifyOtp, signOut as supabaseSignOut } from '../lib/supabase';
import type { AuthUser } from '../types';

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  pendingEmail: string | null;
  
  // Actions
  initialize: () => Promise<void>;
  sendOtp: (email: string) => Promise<void>;
  verifyOtp: (token: string) => Promise<void>;
  signOut: () => Promise<void>;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isLoading: false,
  isAuthenticated: false,
  pendingEmail: sessionStorage.getItem('pending_email'),

  initialize: async () => {
    try {
      set({ isLoading: true });
      
      // Get current session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        set({
          user: {
            id: session.user.id,
            email: session.user.email || '',
          },
          isAuthenticated: true,
        });
      }
      
      // Listen for auth changes
      supabase.auth.onAuthStateChange((_event, session) => {
        if (session?.user) {
          set({
            user: {
              id: session.user.id,
              email: session.user.email || '',
            },
            isAuthenticated: true,
          });
        } else {
          set({
            user: null,
            isAuthenticated: false,
          });
        }
      });
    } catch (error) {
      console.error('Auth initialization error:', error);
    } finally {
      set({ isLoading: false });
    }
  },

  sendOtp: async (email: string) => {
    set({ isLoading: true });
    try {
      await signInWithOtp(email);
      sessionStorage.setItem('pending_email', email);
      set({ pendingEmail: email });
    } finally {
      set({ isLoading: false });
    }
  },

  verifyOtp: async (token: string) => {
    const { pendingEmail } = get();
    if (!pendingEmail) {
      throw new Error('No pending email for OTP verification');
    }
    
    set({ isLoading: true });
    try {
      const data = await verifyOtp(pendingEmail, token);
      if (data.user) {
        sessionStorage.removeItem('pending_email');
        set({
          user: {
            id: data.user.id,
            email: data.user.email || '',
          },
          isAuthenticated: true,
          pendingEmail: null,
        });
      }
    } finally {
      set({ isLoading: false });
    }
  },

  signOut: async () => {
    set({ isLoading: true });
    try {
      await supabaseSignOut();
      set({
        user: null,
        isAuthenticated: false,
        pendingEmail: null,
      });
    } finally {
      set({ isLoading: false });
    }
  },

  setLoading: (loading: boolean) => set({ isLoading: loading }),
}));
